/**
 * BulkLinkTeamModal — Person Records P6.
 *
 * Two-pane Drawer (mirrors NewUserDrawer / FtpSourceDrawer):
 *
 *   Pane 1 (form):
 *     - team search → pick a target team
 *     - person search → multi-select persons (chips show selected count)
 *     - optional jersey-number input per selected row
 *     - Submit → calls bulkLinkTeamMembers(team_id, rows)
 *
 *   Pane 2 (result):
 *     - rows with per-person status badge: linked / already_linked /
 *       blocked / errored
 *     - "Copy" report + "Close" button
 *
 * P4 (max 1 CLUB + 1 NATIONAL) violations surface as "blocked" rows
 * without rolling back the rest of the batch — see the backend
 * action `bulk_link_members`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  searchTeams,
  fetchPersons,
  bulkLinkTeamMembers,
} from '../../../../api';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import styles from './BulkLinkTeamModal.module.css';

const DEBOUNCE_MS = 250;

export default function BulkLinkTeamModal({ onClose, onComplete }) {
  // Team picker
  const [teamQuery, setTeamQuery] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const teamTimer = useRef(null);

  // Person multi-select
  const [personQuery, setPersonQuery] = useState('');
  const [personSuggestions, setPersonSuggestions] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]); // [{id, name, jersey?}]
  const personTimer = useRef(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null); // { results: [...], summary: {...} } | null

  // Team search (debounced)
  useEffect(() => {
    if (teamTimer.current) clearTimeout(teamTimer.current);
    if (selectedTeam && teamQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      return undefined;
    }
    if (!teamQuery || teamQuery.length < 2) {
      setTeamSuggestions([]);
      return undefined;
    }
    teamTimer.current = setTimeout(async () => {
      try {
        const res = await searchTeams(teamQuery);
        setTeamSuggestions(res || []);
      } catch (err) {
        console.error('Team search failed:', err);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(teamTimer.current);
  }, [teamQuery, selectedTeam]);

  // Person search (debounced)
  useEffect(() => {
    if (personTimer.current) clearTimeout(personTimer.current);
    if (!personQuery || personQuery.length < 2) {
      setPersonSuggestions([]);
      return undefined;
    }
    personTimer.current = setTimeout(async () => {
      try {
        const res = await fetchPersons({ search: personQuery, limit: 25 });
        const list = res?.results || res || [];
        setPersonSuggestions(list);
      } catch (err) {
        console.error('Person search failed:', err);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(personTimer.current);
  }, [personQuery]);

  const selectTeam = (team) => {
    setSelectedTeam(team);
    setTeamQuery(team.name);
    setTeamSuggestions([]);
  };

  const clearTeam = () => {
    setSelectedTeam(null);
    setTeamQuery('');
    setTeamSuggestions([]);
  };

  const togglePerson = (person) => {
    setSelectedPersons((prev) => {
      const exists = prev.some((p) => p.id === person.id);
      if (exists) return prev.filter((p) => p.id !== person.id);
      return [
        ...prev,
        {
          id: person.id,
          name:
            person.display_name ||
            `${person.first_name || ''} ${person.last_name || ''}`.trim(),
          jersey: '',
        },
      ];
    });
  };

  const updateJersey = (personId, value) => {
    setSelectedPersons((prev) =>
      prev.map((p) => (p.id === personId ? { ...p, jersey: value } : p)),
    );
  };

  const removeSelected = (personId) => {
    setSelectedPersons((prev) => prev.filter((p) => p.id !== personId));
  };

  const canSubmit =
    !submitting && selectedTeam?.id && selectedPersons.length > 0;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const rows = selectedPersons.map((p) => {
        const row = { person_id: p.id };
        const n = parseInt(p.jersey, 10);
        if (Number.isFinite(n)) row.jersey_number = n;
        return row;
      });
      const data = await bulkLinkTeamMembers(selectedTeam.id, rows);
      setResults(data);
      if (onComplete) onComplete(data);
    } catch (err) {
      console.error('Bulk link failed:', err);
      setError(
        err.response?.data?.error || err.response?.data?.detail || err.message,
      );
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, selectedPersons, selectedTeam, onComplete]);

  const copyReport = () => {
    if (!results) return;
    const lines = [`Bulk link to ${selectedTeam?.name || 'team'}:`];
    for (const r of results.results) {
      const name =
        selectedPersons.find((p) => p.id === r.person_id)?.name || r.person_id;
      lines.push(`  [${r.status.toUpperCase()}] ${name}${r.error ? ' — ' + r.error : ''}`);
    }
    const s = results.summary;
    lines.push('');
    lines.push(
      `Linked ${s.linked}, already_linked ${s.already_linked}, blocked ${s.blocked}, errored ${s.errored}.`,
    );
    navigator.clipboard?.writeText(lines.join('\n'));
  };

  // Result phase
  if (results) {
    const personById = new Map(selectedPersons.map((p) => [p.id, p]));
    const body = (
      <>
        <p className={styles.hint}>
          {`Linked ${results.summary.linked}, already linked ${results.summary.already_linked}, blocked ${results.summary.blocked}, errored ${results.summary.errored}.`}
        </p>
        <h3 className={styles.sectionHeading}>{selectedTeam?.name}</h3>
        <div className={styles.resultList}>
          {results.results.map((r) => {
            const person = personById.get(r.person_id);
            return (
              <div key={r.person_id} className={styles.resultRow}>
                <span className={styles.resultName}>
                  {person?.name || r.person_id}
                </span>
                <span
                  className={clsx(styles.statusBadge, styles[`status_${r.status}`])}
                >
                  {r.status.replace('_', ' ')}
                </span>
                {r.error && <span className={styles.resultError}>{r.error}</span>}
              </div>
            );
          })}
        </div>
        <div className={styles.formGroup}>
          <Btn kind="ghost" size="sm" onClick={copyReport}>
            Copy report
          </Btn>
        </div>
      </>
    );
    return (
      <Drawer
        open
        onClose={onClose}
        eyebrow="§ BULK LINK"
        title="Bulk link complete"
        subtitle={selectedTeam?.name}
        ariaLabel="Bulk link results"
        body={body}
        footer={
          <>
            <span className={styles.footerSpacer} />
            <Btn kind="primary" size="sm" onClick={onClose}>
              Done
            </Btn>
          </>
        }
      />
    );
  }

  // Form phase
  const body = (
    <>
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.formGroup}>
        <label className={styles.label}>Team</label>
        <div className={styles.teamPicker}>
          <input
            type="text"
            className={styles.input}
            placeholder="Search teams…"
            value={teamQuery}
            onChange={(e) => setTeamQuery(e.target.value)}
            autoFocus
          />
          {selectedTeam && (
            <Btn kind="ghost" size="sm" onClick={clearTeam}>
              Clear
            </Btn>
          )}
        </div>
        {teamSuggestions.length > 0 && (
          <ul className={styles.suggestList}>
            {teamSuggestions.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={styles.suggestItem}
                  onClick={() => selectTeam(t)}
                >
                  <span>{t.name}</span>
                  {t.team_type && (
                    <span className={styles.teamTypeChip}>{t.team_type}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {selectedTeam && (
          <p className={styles.hint}>
            Selected team:{' '}
            <strong>{selectedTeam.name}</strong>
            {selectedTeam.team_type ? ` (${selectedTeam.team_type})` : ''}
          </p>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Persons to link</label>
        <input
          type="text"
          className={styles.input}
          placeholder="Search persons by name…"
          value={personQuery}
          onChange={(e) => setPersonQuery(e.target.value)}
        />
        {personSuggestions.length > 0 && (
          <ul className={styles.suggestList}>
            {personSuggestions.slice(0, 15).map((p) => {
              const checked = selectedPersons.some((sp) => sp.id === p.id);
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className={clsx(
                      styles.suggestItem,
                      checked && styles.suggestItemChecked,
                    )}
                    onClick={() => togglePerson(p)}
                  >
                    <span>
                      {p.display_name ||
                        `${p.first_name || ''} ${p.last_name || ''}`.trim()}
                    </span>
                    <span className={styles.checkMark}>{checked ? '✓' : ''}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selectedPersons.length > 0 && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Selected ({selectedPersons.length})
          </label>
          <div className={styles.selectedList}>
            {selectedPersons.map((p) => (
              <div key={p.id} className={styles.selectedRow}>
                <span className={styles.selectedName}>{p.name}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.jerseyInput}
                  placeholder="#"
                  value={p.jersey}
                  onChange={(e) => updateJersey(p.id, e.target.value)}
                />
                <Btn kind="ghost" size="sm" onClick={() => removeSelected(p.id)}>
                  Remove
                </Btn>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow="§ BULK LINK"
      title="Bulk link persons to team"
      subtitle="Idempotent — re-runs are safe"
      ariaLabel="Bulk link persons"
      body={body}
      footer={
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>
            Cancel
          </Btn>
          <span className={styles.footerSpacer} />
          <Btn
            kind="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? 'Linking…'
              : `Link ${selectedPersons.length || ''} person${selectedPersons.length === 1 ? '' : 's'}`}
          </Btn>
        </>
      }
    />
  );
}
